// src/components/KanbanFlow.tsx
//
// "Kanban in motion" — a pinned viewport (CSS sticky) holding a wide strip of
// vibrant, colour-coded pipeline columns that pans horizontally as you scroll,
// like a live board being tracked in real time. Matched to the reference's
// KANBAN IN MOTION section. Reduced-motion / mobile degrade to a static,
// horizontally-scrollable strip.
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../styles/KanbanFlow.css";

gsap.registerPlugin(ScrollTrigger);

const COLUMNS: {
  title: string;
  bg: string;
  fg: string;
  n: number;
  cards: { a: string; co: string; role: string }[];
}[] = [
  {
    title: "Applied",
    bg: "linear-gradient(160deg, #6366f1, #4f46e5)",
    fg: "#ffffff",
    n: 2,
    cards: [
      { a: "F", co: "Figma", role: "Product Designer" },
      { a: "L", co: "Linear", role: "Design Engineer" },
    ],
  },
  {
    title: "In Review",
    bg: "linear-gradient(160deg, #d6ff57, #a3e635)",
    fg: "#0a0a12",
    n: 2,
    cards: [
      { a: "V", co: "Vercel", role: "UI Engineer" },
      { a: "R", co: "Retool", role: "Frontend" },
    ],
  },
  {
    title: "Screening",
    bg: "linear-gradient(160deg, #a855f7, #7c3aed)",
    fg: "#ffffff",
    n: 2,
    cards: [
      { a: "S", co: "Stripe", role: "Sr. Frontend" },
      { a: "G", co: "Google", role: "Product" },
    ],
  },
  {
    title: "Interview",
    bg: "linear-gradient(160deg, #22d3ee, #0891b2)",
    fg: "#04121a",
    n: 2,
    cards: [
      { a: "N", co: "Notion", role: "Designer" },
      { a: "At", co: "Atlas", role: "Sr. UX" },
    ],
  },
  {
    title: "Offer",
    bg: "linear-gradient(160deg, #fb923c, #f97316)",
    fg: "#1a0f04",
    n: 1,
    cards: [{ a: "Pe", co: "Perplexity", role: "Product · $165k" }],
  },
  {
    title: "Done",
    bg: "linear-gradient(160deg, #34d399, #059669)",
    fg: "#04140c",
    n: 1,
    cards: [{ a: "Nw", co: "Northwind", role: "Offer accepted 🎉" }],
  },
];

const KanbanFlow: React.FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const small = window.matchMedia?.("(max-width: 720px)").matches;
    if (reduce || small) return; // CSS shows a static scrollable strip

    const ctx = gsap.context(() => {
      const clamp = gsap.utils.clamp(0, 1);
      ScrollTrigger.create({
        trigger: rootRef.current!,
        start: "top top",
        end: "bottom bottom",
        onUpdate: (self) => {
          const p = clamp(self.progress);
          const vw = window.innerWidth;
          // pan the strip from slightly-right to well-left as you scroll
          const x = 0.06 * vw - p * 0.62 * vw;
          if (rowRef.current) rowRef.current.style.transform = `translateX(${x}px)`;
        },
      });
      ScrollTrigger.refresh();
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="kf" ref={rootRef}>
      <div className="kf-pin">
        <div className="kf-topbar">
          <span className="kf-eyebrow">Kanban in motion — live tracking</span>
          <span className="kf-live">
            <span className="kf-live-dot" /> Live tracking
          </span>
        </div>

        <div className="kf-strip" ref={rowRef} style={{ transform: "translateX(6vw)" }}>
          {COLUMNS.map((col) => (
            <div
              className="kf-col"
              key={col.title}
              style={{ background: col.bg, color: col.fg }}
            >
              <div className="kf-col-head">
                <span className="kf-col-name">{col.title}</span>
                <span className="kf-col-n">{col.n}</span>
              </div>
              {col.cards.map((cd, i) => (
                <div className="kf-job" key={i}>
                  <span className="kf-job-av">{cd.a}</span>
                  <span className="kf-job-body">
                    <span className="kf-job-co">{cd.co}</span>
                    <span className="kf-job-role">{cd.role}</span>
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default KanbanFlow;
